/* Input park: the editor's own inputs convert with the editor toolbar round (component-map.md). */
import {
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  UseDisclosureReturn,
  VStack,
} from '@chakra-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { Button } from '@main/complykit/components/button';
import { EditorThemeClasses, Klass, LexicalEditor, LexicalNode } from 'lexical';
import { createContext, useEffect, useMemo, useState } from 'react';

export interface InsertTableCommandPayload {
  readonly columns: string;
  readonly rows: string;
  readonly includeHeaders?: boolean;
}

export interface CellContextShape {
  cellEditorConfig: null | CellEditorConfig;
  cellEditorPlugins: null | JSX.Element | Array<JSX.Element>;
  set: (
    cellEditorConfig: null | CellEditorConfig,
    cellEditorPlugins: null | JSX.Element | Array<JSX.Element>,
  ) => void;
}

export interface CellEditorConfig {
  readonly namespace: string;
  readonly nodes?: ReadonlyArray<Klass<LexicalNode>>;
  readonly onError: (error: Error, editor: LexicalEditor) => void;
  readonly readOnly?: boolean;
  readonly theme?: EditorThemeClasses;
}

export const CellContext = createContext<CellContextShape>({
  cellEditorConfig: null,
  cellEditorPlugins: null,
  set: () => {
    // Empty
  },
});

export function TableContext({ children }: { children: JSX.Element }) {
  const [contextValue, setContextValue] = useState<{
    cellEditorConfig: null | CellEditorConfig;
    cellEditorPlugins: null | JSX.Element | Array<JSX.Element>;
  }>({
    cellEditorConfig: null,
    cellEditorPlugins: null,
  });
  return (
    <CellContext.Provider
      value={useMemo(
        () => ({
          cellEditorConfig: contextValue.cellEditorConfig,
          cellEditorPlugins: contextValue.cellEditorPlugins,
          set: (cellEditorConfig, cellEditorPlugins) => {
            setContextValue({ cellEditorConfig, cellEditorPlugins });
          },
        }),
        [contextValue.cellEditorConfig, contextValue.cellEditorPlugins],
      )}
    >
      {children}
    </CellContext.Provider>
  );
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface InsertTableDialogProps extends UseDisclosureReturn {}

export function InsertTableDialog({ isOpen, onClose }: InsertTableDialogProps): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [rows, setRows] = useState('5');
  const [columns, setColumns] = useState('5');
  const [isDisabled, setIsDisabled] = useState(true);

  useEffect(() => {
    const row = Number(rows);
    const column = Number(columns);
    if (row && row > 0 && row <= 500 && column && column > 0 && column <= 50) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [rows, columns]);

  function insertTable() {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns, rows });
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Insert Table</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack>
            <FormControl>
              <FormLabel>Rows</FormLabel>
              <Input
                type="number"
                placeholder="# of rows (1-500)"
                value={rows}
                onChange={(e) => setRows(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Columns</FormLabel>
              <Input
                type="number"
                placeholder="# of columns (1-50)"
                value={columns}
                onChange={(e) => setColumns(e.target.value)}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="soft" onPress={onClose}>
            Cancel
          </Button>
          <Button onPress={insertTable} isDisabled={isDisabled}>
            Insert
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
